import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ilikeAnyFilter } from "@/lib/searchFilter";

export interface Client {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  tags: string[];
  source: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpsertClientData {
  id?: string;
  name: string;
  email: string;
  phone?: string | null;
  company?: string | null;
  tags?: string[];
  source?: string | null;
  notes?: string | null;
}

export function useClients(search?: string) {
  return useQuery({
    queryKey: ["clients", search],
    queryFn: async () => {
      let query = supabase
        .from("clients" as never)
        .select("*")
        .order("name", { ascending: true });
      if (search) {
        query = query.or(ilikeAnyFilter(["name", "email", "company"], search));
      }
      const { data, error } = await query;
      if (error) throw error;
      return data as unknown as Client[];
    },
  });
}

export function useClient(id: string | null | undefined) {
  return useQuery({
    queryKey: ["client", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("clients" as never)
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data as unknown as Client;
    },
    enabled: !!id,
  });
}

export function useClientLeads(clientId: string | null | undefined) {
  return useQuery({
    queryKey: ["client-leads", clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from("leads" as never)
        .select("*")
        .eq("client_id", clientId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Array<{
        id: string;
        name: string;
        email: string;
        message: string;
        status: string;
        created_at: string;
      }>;
    },
    enabled: !!clientId,
  });
}

export function useClientBudgets(clientId: string | null | undefined) {
  return useQuery({
    queryKey: ["client-budgets", clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from("budgets" as never)
        .select("id, title, status, net_total, created_at, updated_at, lead_id")
        .eq("client_id", clientId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Array<{
        id: string;
        title: string;
        status: string;
        net_total: number;
        created_at: string;
        updated_at: string;
        lead_id: string | null;
      }>;
    },
    enabled: !!clientId,
  });
}

export function useClientProjectsForClient(clientId: string | null | undefined) {
  return useQuery({
    queryKey: ["client-projects-for-client", clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from("client_projects")
        .select("id, title, phase, priority, budget, due_date, created_at, lead_id")
        .eq("client_id", clientId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!clientId,
  });
}

export function useUpsertClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpsertClientData) => {
      const payload = {
        name: data.name,
        email: data.email.toLowerCase(),
        phone: data.phone ?? null,
        company: data.company ?? null,
        tags: data.tags ?? [],
        source: data.source ?? null,
        notes: data.notes ?? null,
      };
      if (data.id) {
        const { data: row, error } = await supabase
          .from("clients" as never)
          .update(payload as never)
          .eq("id", data.id)
          .select()
          .single();
        if (error) throw error;
        return row as unknown as Client;
      }
      // Plain insert: upsert on email silently overwrote an existing client's
      // company/tags/notes with the (mostly empty) new-client form.
      const { data: row, error } = await supabase
        .from("clients" as never)
        .insert(payload as never)
        .select()
        .single();
      if (error?.code === "23505") {
        throw new Error("já existe um cliente com este e-mail — abra o cadastro dele para editar");
      }
      if (error) throw error;
      return row as unknown as Client;
    },
    onSuccess: (client) => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["client", client.id] });
      toast.success("Cliente salvo!");
    },
    onError: (e: Error) => toast.error("Erro: " + e.message),
  });
}

// Hard delete de um cliente. Todo FK que aponta pra clients (leads, projects,
// payment_installments, payments) é ON DELETE SET NULL — os registros
// relacionados continuam existindo, só perdem o vínculo com este cliente.
export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("clients" as never)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Cliente excluído!");
    },
    onError: (e: Error) => toast.error("Erro ao excluir cliente: " + e.message),
  });
}
